import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import MUIButton from "@/components/ui/Button";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import type { ApproveSummary, MappingRecapRow } from "../../utils/approveSummary";

interface ApproveConfirmDialogProps {
  open: boolean;
  isApproving: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** "Midwest Healthcare Group · Summit Re 2026 · Medical stop-loss claims" */
  subtitle: string;
  summary: ApproveSummary;
  mapping: MappingRecapRow[];
  /** "Sep 30, 2026" or null when no paid-through date applies / is set. */
  paidThroughLabel: string | null;
  /** Template name; "Automatic extraction" until saved layouts ship (#7438). */
  layoutLabel: string;
  /** How many rail checks the reviewer acknowledged — recorded with the approval (#7440 AC6). */
  acknowledgementCount: number;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The approve confirm, ALWAYS shown (#7440, artboard 1i): what the load will
 * do, which columns feed the money and dates, the paid-through and layout
 * recap, and that re-uploading will not undo it. No checkbox — the rail's
 * checks are the gate; this is the last look.
 *
 * Replaces the warnings-only "Approve with Warnings?" confirm, which opened
 * only when soft signals existed and said nothing about the load itself.
 */
export const ApproveConfirmDialog = ({
  open,
  isApproving,
  onClose,
  onConfirm,
  subtitle,
  summary,
  mapping,
  paidThroughLabel,
  layoutLabel,
  acknowledgementCount,
}: ApproveConfirmDialogProps) => (
  <Dialog
    open={open}
    onClose={isApproving ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    aria-labelledby="approve-confirm-title"
  >
    {/* MUI puts the labelledby id on the DialogTitle itself, so the subtitle
        lives in the content: the dialog's accessible name is the question alone. */}
    <DialogTitle id="approve-confirm-title" sx={{ pb: 0.5, fontWeight: 600 }}>
      Approve and load this file?
    </DialogTitle>
    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
        {subtitle}
      </Typography>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          This will
        </Typography>
        <Box component="dl" sx={{ m: 0 }} data-testid="approve-will-rows">
          {summary.rows.map((row) => (
            <Box
              key={row.key}
              sx={{
                display: "grid",
                gridTemplateColumns: "190px minmax(0, 1fr)",
                gap: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography component="dt" variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                sx={{ m: 0, fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {summary.partial && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Counts cover the {summary.loadedClaimants.toLocaleString()} loaded{" "}
            {plural(summary.loadedClaimants, "claimant", "claimants")}
            {summary.totalClaimants !== null
              ? `; the whole file has ${summary.totalClaimants.toLocaleString()} claimants`
              : " — the whole file loads on approve"}
            .
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: "action.hover",
          borderRadius: 2,
          px: 1.5,
          py: 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Money and date columns
        </Typography>
        {mapping.map((m) => (
          <Box
            key={m.key}
            sx={{ display: "grid", gridTemplateColumns: "110px 18px minmax(0, 1fr)", alignItems: "baseline" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {m.field}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ←
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: m.source ? "ui-monospace, Menlo, monospace" : undefined,
                fontSize: 12,
                color: m.source ? "text.primary" : "text.disabled",
              }}
            >
              {m.source ? `“${m.source}”` : "Not mapped"}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1.5 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "128px minmax(0, 1fr)" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Paid through
          </Typography>
          <Typography variant="body2">{paidThroughLabel ?? "Not set"}</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "128px minmax(0, 1fr)" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Layout
          </Typography>
          <Typography variant="body2">{layoutLabel}</Typography>
        </Box>
        {acknowledgementCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {acknowledgementCount.toLocaleString()} {plural(acknowledgementCount, "check", "checks")} you acknowledged{" "}
            {plural(acknowledgementCount, "is", "are")} recorded with this approval.
          </Typography>
        )}
      </Box>

      <Alert severity="warning" role="note" sx={{ "& .MuiAlert-message": { fontSize: 13 } }}>
        Re-uploading a corrected file won&apos;t fix this load. To undo it, an admin has to delete the document.
      </Alert>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 1.75, borderTop: 1, borderColor: "divider" }}>
      <MUIButton variant={ButtonStyleType.Outlined} onClick={onClose} disabled={isApproving}>
        Back to review
      </MUIButton>
      <MUIButton variant={ButtonStyleType.Contained} onClick={onConfirm} disabled={isApproving}>
        {isApproving ? "Approving..." : "Approve and load"}
      </MUIButton>
    </DialogActions>
  </Dialog>
);
